<script setup lang="ts">
import VarsityFeedbackLoading from "@/components/feedback/VarsityFeedbackLoading.vue"
import { computed, inject, onMounted, ref, watch, type ComponentPublicInstance, type Ref } from "vue"

defineOptions({
    inheritAttrs: false,
})

const props = defineProps<{
    id: string | number
    title: string
}>()

const accordionContext = inject<{
    activeTab: Ref<string | number | null>
    selectTab: (id: string | number) => void
    registerTab: (id: string | number, element: HTMLElement | ComponentPublicInstance | null) => void
    layout: "frontend" | "backend" | "list"
    lazy: boolean
    preloadedItems: Ref<Array<string | number>>
}>("accordion-context")

if (!accordionContext) {
    throw new Error("AccordionItem must be used within an Accordion component")
}

const { activeTab, selectTab, registerTab, preloadedItems } = accordionContext

const tabRef = ref<HTMLElement | ComponentPublicInstance | null>(null)
const hasBeenOpened = ref(false)

onMounted(() => {
    registerTab(props.id, tabRef.value as HTMLElement | ComponentPublicInstance | null)

    // If the tab is initially open, mark it as opened
    if (activeTab.value === props.id) {
        hasBeenOpened.value = true
    }
})

watch(activeTab, newValue => {
    if (newValue === props.id && !hasBeenOpened.value) {
        hasBeenOpened.value = true
    }
})

const shouldLoadContent = computed(() => {
    return activeTab.value === props.id || hasBeenOpened.value || preloadedItems.value.includes(props.id)
})
</script>

<template>
    <template v-if="accordionContext.layout === 'list'">
        <li :class="{ active: activeTab === id }">
            <h2 @click="selectTab(id)">{{ title }}</h2>
            <div class="accordion-content-container" :class="{ open: activeTab === id }">
                <div class="animate-show">
                    <slot />
                </div>
            </div>
        </li>
    </template>
    <template v-else>
        <div
            class="accordion-enumeration-card"
            ref="tabRef"
            :class="{ 'accordion-enumeration-card--active': activeTab === id }"
        >
            <div class="accordion-enumeration-card-top" @click="selectTab(id)">
                <template v-if="accordionContext.layout === 'frontend'">
                    <h3>{{ title }}</h3>
                    <div class="accordion-enumeration-card-toggle" :class="{ active: activeTab === id }">
                        <i class="fa fa-fw fa-plus"></i>
                    </div>
                </template>
                <template v-if="accordionContext.layout === 'backend'">
                    <slot name="header">{{ title }}</slot>
                </template>
            </div>

            <!-- For lazy loading: keep content in DOM, animate with CSS -->
            <template v-if="accordionContext.lazy">
                <div v-if="shouldLoadContent" class="accordion-content-container" :class="{ open: activeTab === id }">
                    <div class="animate-show">
                        <div class="accordion-enumeration-card-content" :class="$attrs.class">
                            <Suspense :key="id">
                                <template #default>
                                    <slot />
                                </template>
                                <template #fallback>
                                    <VarsityFeedbackLoading v-if="activeTab === id" class="my-5" />
                                </template>
                            </Suspense>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Non-lazy: use v-animate-show as before -->
            <template v-else>
                <div v-animate-show="activeTab === id">
                    <div class="accordion-enumeration-card-content" :class="$attrs.class">
                        <slot />
                    </div>
                </div>
            </template>
        </div>
    </template>
</template>

<style scoped>
.accordion-content-container {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.5s;
}

.accordion-content-container.open {
    grid-template-rows: 1fr;
}

.accordion-content-container > .animate-show {
    min-height: 0;
    overflow: hidden;
}
</style>
